import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowLeft, Info, Plus, Save } from 'lucide-react';
import { botMessagesApi } from '../../../api';
import { useUIStore } from '../../../store';

const emptyForm = {
  trigger_msgs: '',
  reply_message: '',
  chat_start_msg: '',
  chat_close_msg: '',
  chat_cancel_msg: '',
  chat_end_msg: '',
  chat_close_words: '',
  is_active: true,
};

const parseListInput = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  const trimmed = String(value).trim();
  if (!trimmed) return [];
  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      return trimmed.split(',').map((item) => item.trim()).filter(Boolean);
    }
  }
  return trimmed.split(',').map((item) => item.trim()).filter(Boolean);
};

const formatListValue = (value) => {
  if (!value) return '';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
};

const BotMessagesPage = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const showSnackbar = useUIStore((state) => state.showSnackbar);

  const isEditing = useMemo(() => editingId !== null, [editingId]);

  const resetForm = useCallback(() => {
    setForm(emptyForm);
    setEditingId(null);
  }, []);

  const loadMessages = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await botMessagesApi.list();
      const payload = response?.data?.data ?? response?.data ?? [];
      const rows = Array.isArray(payload) ? payload : [];
      setMessages(rows);
      if (rows.length > 0) {
        const message = rows[0];
        setEditingId(message.bm_id);
        setForm({
          trigger_msgs: formatListValue(message.trigger_msgs),
          reply_message: message.reply_message || '',
          chat_start_msg: message.chat_start_msg || '',
          chat_close_msg: message.chat_close_msg || '',
          chat_cancel_msg: message.chat_cancel_msg || '',
          chat_end_msg: message.chat_end_msg || '',
          chat_close_words: formatListValue(message.chat_close_words),
          is_active: Number(message.is_active) === 1,
        });
      } else {
        resetForm();
      }
    } catch (err) {
      setError('Failed to load bot messages.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    if (!notice) return;
    showSnackbar({ message: notice, severity: 'success' });
    setNotice('');
  }, [notice, showSnackbar]);

  const handleChange = (field) => (event) => {
    const value = event?.target?.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setNotice('');
    try {
      if (!isEditing && messages.length > 0) {
        setError('Only one bot message is allowed.');
        return;
      }
      const triggerList = parseListInput(form.trigger_msgs);
      const closeWords = parseListInput(form.chat_close_words);
      const normalizedTriggers = new Set(triggerList.map((item) => item.toLowerCase()));
      const overlap = closeWords.find((item) => normalizedTriggers.has(item.toLowerCase()));
      if (overlap) {
        setError('Trigger keywords and chat close keywords cannot overlap.');
        return;
      }
      const payload = {
        trigger_msgs: triggerList,
        reply_message: form.reply_message,
        chat_start_msg: form.chat_start_msg || null,
        chat_close_msg: form.chat_close_msg || null,
        chat_cancel_msg: form.chat_cancel_msg || null,
        chat_end_msg: form.chat_end_msg || null,
        chat_close_words: closeWords.length ? closeWords : null,
        is_active: form.is_active ? 1 : 0,
      };

      if (isEditing) {
        await botMessagesApi.update(editingId, payload);
        setNotice('Bot message updated.');
      } else {
        await botMessagesApi.create(payload);
        setNotice('Bot message created.');
      }

      resetForm();
      await loadMessages();
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to save bot message.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" mb={3}>
        <Button variant="outlined" startIcon={<ArrowLeft size={18} />} href="/settings">
          Back
        </Button>
        <Typography variant="h5" fontWeight={700}>
          Bot Messages
        </Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Alert severity="info" icon={<Info size={16} />} sx={{ mb: 2 }}>
        Trigger keywords and chat close keywords must be different.
      </Alert>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2} mb={2}>
            <Typography variant="h6">
              {loading ? 'Loading Bot Message...' : isEditing ? 'Edit Bot Message' : 'Create Bot Message'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Define trigger keywords and automated replies for your bot.
            </Typography>
          </Stack>

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <TextField
                label="Trigger Keywords"
                helperText="Comma-separated triggers or JSON array."
                value={form.trigger_msgs}
                onChange={handleChange('trigger_msgs')}
                required
                fullWidth
                disabled={loading}
              />
              <TextField
                label="Reply Message"
                value={form.reply_message}
                onChange={handleChange('reply_message')}
                required
                multiline
                minRows={3}
                fullWidth
                disabled={loading}
              />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Chat Start Message"
                  value={form.chat_start_msg}
                  onChange={handleChange('chat_start_msg')}
                  fullWidth
                  disabled={loading}
                />
                <TextField
                  label="Chat Close Message"
                  value={form.chat_close_msg}
                  onChange={handleChange('chat_close_msg')}
                  fullWidth
                  disabled={loading}
                />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Chat Cancel Message"
                  value={form.chat_cancel_msg}
                  onChange={handleChange('chat_cancel_msg')}
                  fullWidth
                  disabled={loading}
                />
                <TextField
                  label="Chat End Message"
                  value={form.chat_end_msg}
                  onChange={handleChange('chat_end_msg')}
                  fullWidth
                  disabled={loading}
                />
              </Stack>
              <TextField
                label="Chat Close Keywords"
                helperText="Comma-separated close words or JSON array."
                value={form.chat_close_words}
                onChange={handleChange('chat_close_words')}
                fullWidth
                disabled={loading}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end">
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={isEditing ? <Save size={16} /> : <Plus size={16} />}
                  disabled={saving || loading}
                >
                  {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Message'}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default BotMessagesPage;
